import React from 'react';
import { useParams, Link, Outlet, useLocation } from 'react-router-dom';
import { Calendar, BarChart3 } from 'lucide-react';

interface RosterLayoutProps {
  children?: React.ReactNode;
}

const RosterLayout: React.FC<RosterLayoutProps> = ({ children }) => {
  const { teamId, weekNum, espnLeagueId } = useParams<{ teamId: string; weekNum?: string; espnLeagueId: string }>();
  const location = useLocation();
  // const context = useContext(TeamContext) as TeamContextData;

  // Determine current view mode and week from URL
  const isSeasonView = location.pathname.includes('/seasonoverview');
  const isWeekView = location.pathname.includes('/week/');
  const currentWeek = weekNum ? parseInt(weekNum) : 14;

  // Validate week number and redirect if invalid
  React.useEffect(() => {
    if (isWeekView && weekNum) {
      const week = parseInt(weekNum);
      if (isNaN(week) || week < 1 || week > 18) {
        // Invalid week, redirect to default week
        window.location.replace(`/leagues/espn/${espnLeagueId}/teams/${teamId}/roster/week/14`);
      }
    }
  }, [weekNum, isWeekView, teamId, espnLeagueId]);

  const tabs = [
    {
      id: 'week' as const,
      label: 'Week View',
      icon: Calendar,
      description: 'Weekly roster snapshot with start/sit analysis',
      path: `/leagues/espn/${espnLeagueId}/teams/${teamId}/roster/week/${currentWeek}`,
      active: isWeekView
    },
    {
      id: 'season' as const,
      label: 'Season Overview',
      icon: BarChart3,
      description: 'Draft summary and roster evolution timeline',
      path: `/leagues/espn/${espnLeagueId}/teams/${teamId}/roster/seasonoverview`,
      active: isSeasonView
    }
  ];

  return (
    <div className="space-y-6">
      {/* View Toggle Navigation */}
      <div className="flex items-center space-x-1 bg-background-secondary p-1 rounded-lg w-fit border border-fieldline">
        {tabs.map(({ id, label, icon: Icon, description, path, active }) => (
          <Link
            key={id}
            to={path}
            className={`px-4 py-2 rounded-md flex items-center space-x-2 transition-all duration-200 ${
              active
                ? 'bg-accent-primary/15 text-accent-primary border-b-2 border-accent-primary'
                : 'text-text-secondary hover:text-text-primary hover:bg-background-hover'
            }`}
            title={description}
          >
            <Icon className="w-4 h-4" />
            <span className="font-medium">{label}</span>
          </Link>
        ))}
      </div>

      {/* Week Selector - Only show for week view */}
      {isWeekView && (
        <WeekSelector
          teamId={teamId!}
          espnLeagueId={espnLeagueId!}
          selectedWeek={currentWeek}
          maxWeek={18}
          completedWeeks={[...Array.from({ length: 14 }, (_, i) => i + 1), 16, 17]}
        />
      )}

      {/* Outlet for nested routes */}
      <Outlet />
    </div>
  );
};

// Week Selector Component for URL-based navigation
interface WeekSelectorProps {
  teamId: string;
  espnLeagueId: string;
  selectedWeek: number;
  maxWeek: number;
  completedWeeks: number[];
}

const WeekSelector: React.FC<WeekSelectorProps> = ({
  teamId,
  espnLeagueId,
  selectedWeek,
  maxWeek,
  completedWeeks
}) => {
  const weeks = Array.from({ length: maxWeek }, (_, i) => i + 1);

  const isWeekCompleted = (week: number) => completedWeeks.includes(week);

  return (
    <div className="flex items-center space-x-4">
      {/* Previous Week Button */}
      <Link
        to={`/leagues/espn/${espnLeagueId}/teams/${teamId}/roster/week/${Math.max(1, selectedWeek - 1)}`}
        className={`p-2 rounded-lg transition-colors ${
          selectedWeek > 1
            ? 'bg-background-tertiary hover:bg-background-hover text-text-secondary'
            : 'bg-background-secondary text-text-tertiary cursor-not-allowed pointer-events-none'
        }`}
        aria-disabled={selectedWeek <= 1}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </Link>

      {/* Week Selector */}
      <div className="flex items-center space-x-2 bg-background-secondary border border-fieldline rounded-lg p-2">
        <span className="text-text-secondary text-sm font-medium">Week:</span>
        <div className="flex items-center space-x-1">
          {weeks.map(week => (
            <Link
              key={week}
              to={`/leagues/espn/${espnLeagueId}/teams/${teamId}/roster/week/${week}`}
              className={`w-8 h-8 rounded flex items-center justify-center text-sm font-medium transition-all ${
                week === selectedWeek
                  ? 'bg-accent-primary text-background-primary shadow-lg'
                  : isWeekCompleted(week)
                    ? 'bg-background-tertiary text-text-secondary hover:bg-background-hover'
                    : 'bg-background-secondary text-text-tertiary hover:bg-background-hover'
              }`}
              title={`Week ${week}${isWeekCompleted(week) ? ' (Completed)' : ''}`}
            >
              {week}
            </Link>
          ))}
        </div>
      </div>

      {/* Next Week Button */}
      <Link
        to={`/leagues/espn/${espnLeagueId}/teams/${teamId}/roster/week/${Math.min(maxWeek, selectedWeek + 1)}`}
        className={`p-2 rounded-lg transition-colors ${
          selectedWeek < maxWeek
            ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
            : 'bg-slate-800 text-slate-600 cursor-not-allowed pointer-events-none'
        }`}
        aria-disabled={selectedWeek >= maxWeek}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </div>
  );
};

export default RosterLayout;